import React, { useState } from 'react';
import { 
  TextField, 
  TextFieldProps, 
  IconButton,
  FormControl,
  Tooltip,
  InputAdornment
} from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';

interface ExpandableTextFieldProps extends Omit<TextFieldProps, 'rows'> {
  defaultRows?: number;
  expandedRows?: number;
}

const ExpandableTextField: React.FC<ExpandableTextFieldProps> = ({
  defaultRows = 3,
  expandedRows = 15,
  id,
  InputProps,
  ...rest
}) => {
  const [expanded, setExpanded] = useState(false);
  
  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  // Use a unique ID for the input if not provided
  const inputId = id || `expandable-text-field-${Math.random().toString(36).substring(2, 9)}`;
  
  // Create the expansion button that will be placed at the top-right
  const ExpansionButton = (
    <Tooltip title={expanded ? "Collapse" : "Expand"}>
      <IconButton
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleExpand();
        }}
        size="small"
        sx={{
          position: 'absolute',
          top: '-16px',
          right: '16px',
          height: '32px',
          width: '32px',
          backgroundColor: 'white',
          border: '1px solid rgba(0, 0, 0, 0.12)',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          zIndex: 1
        }}
        aria-label={expanded ? "collapse text field" : "expand text field"}
      >
        {expanded ? 
          <CloseFullscreenIcon sx={{ fontSize: '24px' }} /> : 
          <OpenInFullIcon sx={{ fontSize: '24px' }} />
        }
      </IconButton>
    </Tooltip>
  );

  return (
    <FormControl fullWidth sx={{ position: 'relative' }}>
      {/* Add the expansion button outside of TextField to avoid InputLabel issues */}
      {ExpansionButton}
      
      <TextField
        {...rest}
        id={inputId}
        multiline
        rows={expanded ? expandedRows : defaultRows}
        InputProps={{
          ...InputProps
        }}
        // Add a smooth transition effect when expanding/collapsing
        sx={{
          transition: 'all 0.3s ease-in-out',
          ...(rest.sx || {})
        }}
      />
    </FormControl>
  );
};

export default ExpandableTextField;